import type { Strategy } from '../strategies/strategies.d';
import type { Crop } from '../crops/crops.d';
import type { Treatment } from '../treatments/treatments.d';
import { PurposeType, SearchModel } from './search';

type ApiResponse = {
    name: string;
    slug: string;
};

const getRegex = (searchTerm: string) => new RegExp(`.?${searchTerm.replace(/[^a-zåäöé0-9-_]/, '')}.?`, 'ig');

export const getRelatedModelsCount = (strategies: Strategy[]) => {
    const totalCrops = new Set<string>();
    const totalProducts = new Set<string>();
    const totalTreatments = new Set<string>();

    strategies.forEach(({ crop, treatments, products }) => {
        totalCrops.add(crop.slug);
        products.forEach(({ slug }) => totalProducts.add(slug));
        treatments.forEach(({ slug }) => totalTreatments.add(slug));
    });

    return {
        crops: totalCrops.size,
        products: totalProducts.size,
        treatments: totalTreatments.size,
    };
};

export const getAllProductsForStrategies = (strategies: Strategy[]) => {
    const filteredProducts: { name: string; slug: string }[] = [];

    strategies.forEach(({ products }) =>
        products.forEach(({ name, slug }) => {
            if (!filteredProducts.find((product) => product.slug === slug)) {
                filteredProducts.push({ name, slug });
            }
        })
    );

    return filteredProducts.sort((a, b) => a.name.localeCompare(b.name, 'sv'));
};

export const getAllPurposesAndTreatmentForStrategies = (strategies: Strategy[], purposes: PurposeType[], searchTerm: string = '') => {
    const regEx = getRegex(searchTerm);
    const filteredPurposes: PurposeType[] = [...purposes]
        .filter((purpose) => purpose.name.match(regEx))
        .map((purpose) => ({ ...purpose, treatments: [] }));

    strategies.forEach((strategy) => {
        strategy.treatments.forEach((treatment) => {
            const purpose = purposes.find((purpose) => !!purpose.treatments.find((t) => t.slug === treatment.slug));
            if (!purpose) {
                return;
            }

            const filteredPurpose = filteredPurposes.find((p) => p.slug === purpose.slug);
            if (!filteredPurpose) {
                filteredPurposes.push({ ...purpose, treatments: [treatment] });
                return;
            }

            if (!filteredPurpose.treatments.find((t) => t.slug === treatment.slug)) {
                filteredPurpose.treatments.push(treatment);
            }
        });
    });

    return filterPurposesWithTreatments(filteredPurposes, searchTerm);
};

export const getFilteredStrategies = (
    strategies: Strategy[],
    filters: { crop: Crop; treatment: Treatment | null; purpose: PurposeType | null }
) =>
    strategies.filter((strategy) => {
        if (filters.crop.slug !== strategy.crop.slug) {
            return false;
        }

        if (filters.treatment) {
            return strategy.treatments.some((t) => t.slug === filters?.treatment?.slug);
        }

        if (filters.purpose) {
            return filters.purpose?.treatments?.some((treatment) =>
                strategy.treatments.some((strategyTreatment) => strategyTreatment.slug === treatment.slug)
            );
        }

        return false;
    });

export const filterPurposesWithTreatments = (purposes: PurposeType[], searchTerm = '') => {
    const regEx = getRegex(searchTerm);
    return purposes
        .filter((purpose) => {
            if (!searchTerm) {
                return purpose;
            }

            if (purpose.treatments.some(({ name }) => name.match(regEx))) {
                return true;
            }

            return purpose.name.match(regEx);
        })
        .map((purpose) => {
            if (!searchTerm) {
                return purpose;
            }

            const treatments = purpose.treatments.filter(({ name }) => name.match(regEx));
            return {
                ...purpose,
                treatments,
            };
        })
        .map((purpose) => ({
            ...purpose,
            treatments: purpose.treatments.sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        }))
        .sort((a, b) => a.name.localeCompare(b.name, 'sv'));
};

export const filterRelatedModels = (
    strategies: Strategy[],
    purposes: PurposeType[],
    searchTerm = '',
    filterType: Omit<SearchModel, 'product'> = 'crop'
) => {
    const regEx = getRegex(searchTerm);
    const filteredProducts: ApiResponse[] = [];
    const filteredCrops: ApiResponse[] = [];
    const filteredPurposes = filterType === 'crop' ? getAllPurposesAndTreatmentForStrategies(strategies, purposes, searchTerm) : [];

    strategies.forEach(({ crop, products }) => {
        products
            .filter((product) => product.name.match(regEx))
            .forEach(({ name, slug }) => {
                if (!filteredProducts.find((product) => product.slug === slug)) {
                    filteredProducts.push({ name, slug });
                }
            });

        // Implicitly there is a treatment selected, so check if the crop matches
        if (crop.name.match(regEx) && !filteredCrops.find((newCrop) => newCrop.slug === crop.slug)) {
            filteredCrops.push({
                name: crop.name,
                slug: crop.slug,
            });
        }
    });

    return {
        filteredPurposes,
        filteredProducts: filteredProducts.sort((a, b) => a.name.localeCompare(b.name, 'sv')),
        filteredCrops: filteredCrops.sort((a, b) => a.name.localeCompare(b.name, 'sv')),
    };
};
